import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/lib/api';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      tenantModules: [],

      login: async (email, password) => {
        const res = await api.post('/auth/login', { email, password });
        const { access_token, user, tenant_modules, onboarding_completed } = res.data;
        const userData = { ...user, onboarding_completed: onboarding_completed !== undefined ? onboarding_completed : true };
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('tenant_modules', JSON.stringify(tenant_modules || []));
        set({ user: userData, token: access_token, isAuthenticated: true, tenantModules: tenant_modules || [] });
        return res.data;
      },

      logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tenant_modules');
        set({ user: null, token: null, isAuthenticated: false, tenantModules: [] });
      },

      setUser: (user) => {
        localStorage.setItem('user', JSON.stringify(user));
        set({ user });
      },

      setTenantModules: (modules) => {
        localStorage.setItem('tenant_modules', JSON.stringify(modules || []));
        set({ tenantModules: modules || [] });
      },

      // Hydrate from localStorage on first load (for backward compatibility)
      hydrate: () => {
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        const tmStr = localStorage.getItem('tenant_modules');
        if (token && userStr) {
          try {
            const user = JSON.parse(userStr);
            const tenantModules = tmStr ? JSON.parse(tmStr) : [];
            set({ user, token, isAuthenticated: true, tenantModules });
          } catch {
            set({ user: null, token: null, isAuthenticated: false, tenantModules: [] });
          }
        }
      },

      hasPermission: (module) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === 'admin' || user.role === 'manager') return true;
        const perms = user.permissions || [];
        return perms.includes(module);
      },

      hasModuleAccess: (moduleKey) => {
        const { user, tenantModules } = get();
        if (!user) return false;
        if (user.role === 'admin' || user.is_super_admin) return true;
        // Alias mapping: legacy sidebar names -> catalog names
        const ALIASES = {
          hr: 'workforce', pos: 'sales', factory: 'operations',
          reports: 'analytics', stock: 'inventory', expenses: 'procurement',
          finance: 'accounting', admin: 'core',
        };
        const keysToCheck = [moduleKey];
        if (ALIASES[moduleKey]) keysToCheck.push(ALIASES[moduleKey]);
        // Also check reverse: if moduleKey is a catalog name, check legacy aliases
        const REV = { workforce: 'hr', sales: 'pos', operations: 'factory', analytics: 'reports', inventory: 'stock', procurement: 'expenses', core: 'admin' };
        if (REV[moduleKey]) keysToCheck.push(REV[moduleKey]);

        const userModules = user.allowed_modules || [];
        if (userModules.length > 0 && !keysToCheck.some(k => userModules.includes(k)) && !userModules.includes('all')) return false;
        if (tenantModules.length > 0 && !keysToCheck.some(k => tenantModules.includes(k)) && !tenantModules.includes('all')) return false;
        return true;
      },

      isBranchRestricted: () => {
        const { user } = get();
        return user?.branch_id && user.branch_id !== '';
      },

      getUserBranch: () => {
        const { user } = get();
        return user?.branch_id || null;
      },
    }),
    {
      name: 'ssc-auth',
      partialize: (state) => ({ user: state.user, token: state.token, isAuthenticated: state.isAuthenticated, tenantModules: state.tenantModules }),
    }
  )
);
